import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { type CMSBadgeData, type StoreLogo } from '@/features/cms/components/CMS/types'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { TransparentAnchor, TransparentButton } from '@/features/shared/elements/Buttons'
import { getBoxShadow } from '@/features/shared/styles/effects'
import { Badge } from '@/features/cms/components/CMS/components/Shelves/Badge'
import { useSelectPromoStore } from '@/features/cms/components/CMS/hooks/useSelectPromoStore'
import { StoreIcons } from '@/features/cms/components/CMS/components/Shelves/StoreIcons'
import { Body } from '@shipt/design-system-typography'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type Promotion } from '@/features/shop/services/Campaigns/types'
import { TargetedJustForYouFlag } from '@/features/shared/elements/Badges/TargetedJustForYouFlag'
import { theme } from '@/features/shared/constants/shiptTheme'

export const LargePromoItem = ({
  promotion,
  onClick,
  noShadow,
  destinationUrl,
  availableStores,
  badge,
}: {
  promotion: Promotion
  onClick: () => void
  noShadow?: boolean
  destinationUrl: string
  availableStores?: StoreLogo[]
  badge?: CMSBadgeData
}) => {
  const { display_description, display_name, offer_headline, promotion_params } = promotion
  const { image_url } = promotion_params?.images?.[0] ?? {}
  const { selectPromoStore, normalizedUrl, renderLinkInCard } = useSelectPromoStore(
    promotion,
    destinationUrl,
  )

  const handleOnClick = () => {
    selectPromoStore()
    onClick()
  }

  const cardContent = (
    <>
      {badge ? <Badge badge={badge} /> : <TargetedJustForYouFlag promotion={promotion} />}
      <BackgroundImageWrapper>
        <BackgroundImage fill alt="" src={image_url ?? ''} $noShadow={noShadow} sizes="344px" />
        <StoreIcons availableStores={availableStores} />
      </BackgroundImageWrapper>
      <TextWrapper>
        {!!offer_headline && (
          <TitleText strong concept="feedback" feedbackType="promotion" size="xl">
            {offer_headline}
          </TitleText>
        )}
        <TitleText strong size="xl">
          {display_name}
        </TitleText>
        <DescriptionText variant="secondary">{display_description}</DescriptionText>
      </TextWrapper>
    </>
  )

  return (
    <SkeletonConsumer width={344} height={324}>
      <>
        {!!renderLinkInCard && (
          <PromotionButtonLink
            href={normalizedUrl}
            onClick={(e) => {
              e.preventDefault()
              handleOnClick()
            }}
            aria-label={`View ${display_name} promotion`}
            data-no-shadow={noShadow}
          >
            {cardContent}
          </PromotionButtonLink>
        )}
        {!renderLinkInCard && (
          <PromotionButton
            onClick={handleOnClick}
            aria-label={`View ${display_name} promotion`}
            data-no-shadow={noShadow}
          >
            {cardContent}
          </PromotionButton>
        )}
      </>
    </SkeletonConsumer>
  )
}

const promotionButtonStyles = css`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 258px;
  padding: 0 0 0.5rem;
  border-radius: 0.5rem;
  overflow: hidden;
  text-align: left;

  &:not([data-no-shadow='true']) {
    ${getBoxShadow('rest', 'light')};
  }

  @media ${screenSizes.tablet} {
    width: 344px;
  }
`

const PromotionButtonLink = styled(TransparentAnchor)<{
  'data-no-shadow'?: boolean
}>`
  ${promotionButtonStyles}
`

const PromotionButton = styled(TransparentButton)<{
  'data-no-shadow'?: boolean
}>`
  ${promotionButtonStyles}
`

const BackgroundImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 204px;
`

const BackgroundImage = styled(Image)<{ $noShadow?: boolean }>`
  width: 100%;
  object-fit: cover;
  border-radius: ${({ $noShadow }) => ($noShadow ? '0.5rem' : '0')};
`

const TextWrapper = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  background-color: ${theme.white};
  padding: 1rem;
  width: 100%;
`

const TitleText = styled(Body)`
  overflow: hidden;

  @media ${screenSizes.tablet} {
    width: 310px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

export const DescriptionText = styled(Body)<{ lineClamp?: number }>`
  /* stylelint-disable-next-line value-no-vendor-prefix -- -webkit-line-clamp used here is only compatible with -webkit-box so we need to keep using it */
  display: -webkit-box;
  -webkit-line-clamp: ${({ lineClamp }) => lineClamp ?? 2};
  -webkit-box-orient: vertical;
  overflow: hidden;
`
